import { CreateAccountProps } from '@tcs/create-account/components/Steps';
import { mapper } from '@tcs/create-account/mapping/mapper';
import {
  getOptionalDocumentByID,
  getPartnerConfiguration,
} from '@tcs/create-account/utils/prismic/api';
import {
  FooterType,
  HeaderType,
  LandingPageType,
  PageType,
  PartnerConfigurationType,
} from '@tcs/create-account/utils/prismic/types';
import get from 'lodash/get';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PARTNER = 'forge-trust';

type PartnerConfigData = Partial<
  CreateAccountProps & {
    header: HeaderType;
    footer: FooterType;
    landingPage: PageType<LandingPageType>;
  }
>;

export interface PartnerConfig {
  loading: boolean;
  data: PartnerConfigData;
}

const usePartnerConfig = (
  partnerId = DEFAULT_PARTNER,
  representativeCode?: string
): Omit<PartnerConfig, 'setPartnerId'> => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PartnerConfigData | undefined>();

  useEffect(() => {
    if (!partnerId) {
      return;
    }

    const loadConfig = async () => {
      try {
        const config = get(await getPartnerConfiguration(partnerId), 'data');

        if (!config) {
          return;
        }

        const header = await getOptionalDocumentByID<HeaderType>(
          config?.header?.id
        );
        const footer = await getOptionalDocumentByID<FooterType>(
          config?.footer?.id
        );
        const landingPage = await getOptionalDocumentByID<
          PageType<LandingPageType>
        >(config?.landing_page?.id);

        const props = mapper.map<PartnerConfigurationType, CreateAccountProps>(
          config,
          'CreateAccountProps',
          'PartnerConfigurationType',
          {
            extraArguments: {
              representativeCode,
            },
          }
        );

        const debugParam = searchParams.get('debug');

        setData({
          ...props,
          debug: debugParam === 'true' || debugParam === String(),
          header: get(header, 'data'),
          footer: get(footer, 'data'),
          landingPage: get(landingPage, 'data'),
        });
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [partnerId, searchParams]);

  return {
    loading,
    data,
  };
};

export default usePartnerConfig;
